// Recalculate from dated records; never filter precomputed period totals.
const unique = values => [...new Set(values)];
const round = n => Math.round((n + Number.EPSILON) * 100) / 100;
const hours = rows => round(rows.reduce((n, row) => n + row.hours, 0));

export function filterMaintenance(view, source, from, through, vendorRows = []) {
  if (!source) return null;
  const within = day => day && day >= from && day <= through;
  const labor = source.labor.filter(row => within(row.date));
  const orders = source.orders;
  if (view === 'technicians') {
    const summary = source.summary.map(tech => {
      const rows = labor.filter(row => row.technician === tech.technician);
      const days = unique(rows.map(row => row.date)).length;
      const jobs = unique(rows.filter(row => row.work_order_id).map(row => `${row.date}|${row.work_order_id}`)).length;
      return { ...tech, hours: hours(rows), days_logged: days, jobs_per_day: days ? round(jobs / days) : null };
    });
    return { ...source, labor, summary };
  }
  const coverage = {
    occupied_hours: hours((source.coverage_entries || labor).filter(row => within(row.date) && row.occupancy_id)),
    excluded_hours: hours((source.coverage_entries || []).filter(row => within(row.date) && !row.occupancy_id)),
  };
  let rows;
  if (view === 'requests') {
    rows = source.rows.map(row => {
      const jobs = row.order_ids.map(id => orders[id]).filter(job => job && within(job.created));
      const counts = new Map();
      jobs.forEach(job => counts.set(job.issue, (counts.get(job.issue) || 0) + 1));
      return { ...row, order_ids: jobs.map(job => job.id),
        request_days: unique(jobs.map(job => job.created)).length,
        requests: unique(jobs.map(job => job.request_id)).length,
        jobs: jobs.filter(job => !job.canceled).length,
        hours: hours(labor.filter(entry => entry.occupancy_id === row.id)),
        top_issue: [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] || '',
      };
    }).filter(row => row.requests).sort((a, b) => b.request_days - a.request_days || b.requests - a.requests || a.address.localeCompare(b.address));
  } else if (view === 'labor') {
    rows = source.rows.map(row => {
      const entries = labor.filter(entry => entry.property_id === row.id);
      const technicians = Object.fromEntries(unique(entries.map(entry => entry.technician)).sort()
        .map(name => [name, hours(entries.filter(entry => entry.technician === name))]));
      return { ...row, hours: hours(entries), jobs: unique(entries.map(entry => entry.work_order_id)).length,
        order_ids: unique(entries.map(entry => entry.work_order_id).filter(Boolean)).sort(), technicians,
        request_days: unique((source.request_events || []).filter(event => event.property_id === row.id && within(event.created))
          .map(event => `${event.occupancy_id}|${event.created}`)).length,
      };
    }).filter(row => row.order_ids.length).sort((a, b) => b.hours - a.hours || a.address.localeCompare(b.address));
  } else {
    rows = source.rows.map(row => {
      const jobs = row.order_ids.map(id => orders[id]).filter(job => job && within(job.created));
      const ids = jobs.map(job => job.id);
      const units = unique(jobs.map(job => job.unit_id));
      const first = jobs.map(job => job.created).sort()[0];
      const vendors = vendorRows.filter(vendor => vendor.property_id === row.property_id && vendor.tags.includes(row.family) && vendor.date < first)
        .map(vendor => ({ ...vendor, match: units.includes(vendor.unit_id) ? 'Same unit' : 'Property / other unit' }))
        .sort((a, b) => Number(b.match === 'Same unit') - Number(a.match === 'Same unit') || b.date.localeCompare(a.date)).slice(0, 5);
      return { ...row, order_ids: ids, unit_ids: units, first_date: first, units: units.length,
        jobs: ids.length, hours: hours(labor.filter(entry => ids.includes(entry.work_order_id))), vendors };
    }).filter(row => row.jobs >= 2).sort((a, b) => b.jobs - a.jobs || a.address.localeCompare(b.address) || a.family.localeCompare(b.family));
  }
  return { ...source, labor, rows, coverage };
}
